import { useEffect, useState } from 'react';
import { useAccountContext } from '@gonfalon/app-context/useAccountContext';
import { isModernSubscription } from '@gonfalon/billing/subscriptions';
import { isSandbox } from '@gonfalon/constants/constants';
import { trackCreateSupportTicket } from '@gonfalon/contact-support/trackCreateSupportTicket';
import { MarketoForm } from '@gonfalon/marketing-forms/forms';
import { trackContactSalesFormSubmitted } from '@gonfalon/marketo/analytics';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import type { GonfalonWindow } from '@gonfalon/types/GonfalonWindow';
import { NewTabLink } from '@gonfalon/ui/NewTabLink';
import { Button } from '@launchpad-ui/components';
// oxlint-disable-next-line no-restricted-imports
import { ModalBody } from '@launchpad-ui/core';
import cx from 'clsx';

import type { ContactSalesFormProps } from './types';

import styles from './ContactSalesFormLegacy.module.css';

declare global {
  interface Window extends GonfalonWindow {}
}

const baseUrl = '//go.launchdarkly.com';
const munchkinId = '850-KKH-319';
const formId = MarketoForm.ContactSales;

/**
 * Contact sales, rendered by the Marketo client script: the fields, layout, and
 * submit button all come from Marketo's own form HTML, which this styles from
 * the outside. Kept until `release-unified-form-submit` has the contact sales
 * form ID released everywhere, so the cutover can be rolled back without a
 * deploy.
 *
 * @see ContactSalesForm
 */
export function ContactSalesFormLegacy({
  requestedFeature,
  headerText,
  productInterest,
  onFormSubmit,
}: ContactSalesFormProps) {
  const { profile, account, subscription } = useAccountContext();
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [successfullySubmitted, setSuccessfullySubmitted] = useState(false);
  useEffect(() => {
    if (scriptLoaded) {
      window.MktoForms2.loadForm(baseUrl, munchkinId, formId, (form: HTMLFormElement) => {
        if (isModernSubscription(subscription)) {
          form.addHiddenFields({
            requestedfeature: requestedFeature,
            planType: subscription.planType,
            subscriptionState: subscription.state,
          });
        }

        /* eslint-disable no-param-reassign */
        if (isSandbox()) {
          // We do not want to prefill the form with demo user data in sandbox
          form = form.setValues({
            Website_Dr_Button__c: 'dr-product-guardian',
          });
        } else {
          form = form.setValues({
            Email: profile.email || '',
            FirstName: profile.firstName || '',
            LastName: profile.lastName || '',
            Company: account?.organization || '',
            Website_Dr_Button__c: productInterest || '',
          });
          /* eslint-enable no-param-reassign */
        }

        form.onSuccess(() => {
          form.getFormElem().hide();
          setSuccessfullySubmitted(true);
          trackContactSalesFormSubmitted({ url: window.location.href, path: window.location.pathname });
          onFormSubmit?.();

          return false;
        });
      });
    }
    if (window.MktoForms2) {
      return setScriptLoaded(true);
    }

    const script = document.createElement('script');
    script.src = '//go.launchdarkly.com/js/forms2/js/forms2.min.js';
    script.addEventListener('load', () => (window.MktoForms2 ? setScriptLoaded(true) : null));
    document.body.appendChild(script);
  }, [requestedFeature, scriptLoaded]);

  const renderSkeletonFormRow = () => (
    <div className={styles.skeletonScreen}>
      <div className={cx(styles.skeletonLoading, styles.skeletonField)}></div>
      <div className={cx(styles.skeletonLoading, styles.skeletonField)}></div>
    </div>
  );

  window.MktoForms2?.onFormRender(() => {
    //hacky way to hide the skeleton screen once the form finally loads
    const skeletonBody = document.getElementById('renderSkeletonBody');
    if (skeletonBody) {
      skeletonBody.style.display = 'none';
    }
    const element = document.getElementById(`mktoForm_${formId}`);
    element?.classList.remove(styles.hidden);
  });

  const renderPolicyLinks = () => (
    <p className="u-fs-sm">
      <Message
        id="billing.contactSalesLegacy.policyBody"
        values={{
          terms: () => (
            <NewTabLink
              className={styles.marketoLinks}
              href="https://launchdarkly.com/policies/subscription-terms/"
              text={msg('billing.contactSalesLegacy.termsLink')}
            />
          ),
          privacy: () => (
            <NewTabLink
              className={styles.marketoLinks}
              href="https://launchdarkly.com/policies/privacy/"
              text={msg('billing.contactSalesLegacy.privacyLink')}
            />
          ),
        }}
      />
    </p>
  );

  const renderSkeletonBody = () => (
    <div id="renderSkeletonBody">
      {renderSkeletonFormRow()}
      {renderSkeletonFormRow()}
      <div className={cx(styles.skeletonLoading, 'u-flex-center', styles.skeletonCommentField)}></div>
      <div className={styles.skeletonButton}>
        <Button variant="primary">{msg('billing.contactSalesLegacy.submit')}</Button>
      </div>
    </div>
  );

  const renderHeader = () => (
    <h2 className={styles.marketoTitle}>{headerText ?? msg('billing.contactSalesLegacy.defaultHeading')}</h2>
  );

  const renderSkeletonScreen = () => (
    <div>
      {renderHeader()}
      {renderSkeletonBody()}
    </div>
  );

  return (
    <>
      <ModalBody className={styles.modalBody}>
        <div className={cx(styles.form, { [styles.marketoSubmission]: successfullySubmitted })}>
          {scriptLoaded ? (
            <div className={styles.marketoForm}>
              {!successfullySubmitted ? (
                <>
                  {renderHeader()}
                  <p className={styles.contactSales}>
                    <Message
                      id="billing.contactSalesLegacy.supportPrompt"
                      values={{
                        ticket: (chunks) => (
                          <a
                            onClick={() => trackCreateSupportTicket('ContactUsForm')}
                            href="https://support.launchdarkly.com/hc/en-us/requests/new"
                            target="_blank"
                            rel="noreferrer"
                          >
                            {chunks}
                          </a>
                        ),
                      }}
                    />
                  </p>
                  {!window.MktoForms2._polyfillsLoaded && renderSkeletonBody()}
                  <form id={`mktoForm_${formId}`} className={styles.hidden} />
                  {renderPolicyLinks()}
                </>
              ) : (
                <div className={styles.marketoSubmission}>
                  <div>
                    <h1 className="u-fs-xxxl u-pb-s u-fw-bold u-white">
                      <Message id="billing.contactSalesLegacy.thankYouHeading" />
                    </h1>
                    <p className={styles.expertText}>
                      <Message id="billing.contactSalesLegacy.thankYouBody" />
                    </p>
                    <p className="u-fs-sm u-pb-s">
                      <Message id="billing.contactSalesLegacy.responseTime" />
                    </p>
                  </div>
                  <p>
                    <Message
                      id="billing.contactSalesLegacy.learnMoreBody"
                      values={{
                        strong: (chunks) => <span className="u-fw-bold">{chunks}</span>,
                        pricing: (chunks) => (
                          <a className={styles.marketoLinks} href="https://launchdarkly.com/pricing/">
                            {chunks}
                          </a>
                        ),
                      }}
                    />
                  </p>
                </div>
              )}
            </div>
          ) : (
            <>
              {renderSkeletonScreen()}
              {renderPolicyLinks()}
            </>
          )}
        </div>
      </ModalBody>
    </>
  );
}
